import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from "@mui/material"
import Input from "components/controls/input"
import Select from "components/controls/select"
import { action, observable } from "mobx"
import { observer } from "mobx-react-lite"
import Collapse from "pages/content/components/collapse"
import { useMemo, useState } from "react"
import { useMainStore } from "store"
import Content from "store/content"
import { useForm } from "utils/use-form"
import useKeyDown from "utils/use-key-down"
import { useDialogStore } from ".."
import { getOptionalBlocks } from "./utils"

type Props = {
  content?: Content,
  isCopy?: boolean
}

const NewContentBlockDialog = observer(({ content, isCopy }: Props) => {

  const mainStore = useMainStore()
  const dialog = useDialogStore()
  const [ optionalBlocksSet ] = useState(() => observable.set<string>(content?.ignoreBlocks || []))
  const form = useForm(content && { 
    name: content.name, 
    sectionId: content.data.sectionId, 
    slugPrefix: content.data.slugPrefix, 
    schemeId: content.schemeId 
  })
  const disabled = !form.get("name") || !form.get("schemeId")
  const project = mainStore.selectedProject && mainStore.projects.get(mainStore.selectedProject)

  useKeyDown({
    "Enter": () => apply()
  }, [ disabled ])

  const apply = () => {
    if (disabled) return false
    const schemeId = form.get("schemeId")
    if (!project || !schemeId) return false
    
    const newContent = content || new Content(schemeId)
    newContent.name = form.get("name") || ""
    newContent.data.sectionId = form.get("sectionId") || ""
    newContent.data.slugPrefix = form.get("slugPrefix") || ""
    newContent.ignoreBlocks = Array.from(optionalBlocksSet.values())
    newContent.schemeId = schemeId
    
    project.addContent(newContent)
    dialog.close()

    mainStore.saveContent(newContent)
    mainStore.saveProject(project)
    
    return true
  }

  const schemeId = form.get("schemeId")
  const optionalBlocks = useMemo(() => {
    const schema = schemeId && mainStore.schemes.get(schemeId)
    if (!schema) return null
    
    return getOptionalBlocks(schema)
  }, [ schemeId ]) // eslint-disable-line react-hooks/exhaustive-deps

  const _schemeId = form.get("schemeId")
  const _sectionId = form.get("sectionId")
  const simularContent = useMemo(() => {
    if (!project) return
    const contents = Array.from(project.contents.values())
    return contents.find(item => item.schemeId === form.get("schemeId") && item.data.sectionId === (form.get("sectionId") || ""))
  }, [ _schemeId, _sectionId ])  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DialogTitle>{ content? (isCopy? "Дублирование блока": "Изменение данных блока"): "Добавление блока" }</DialogTitle>
      <DialogContent sx={{ width: 550, pb: 1, "&>*:not(:last-child)": { mb: 0.5 } }}>
        <Input autoFocus {...form.getProps("name")} label="Название блока" placeholder="Новый блок" />
        <Input {...form.getProps("sectionId")} label="ID секции" placeholder="Пустое значение" />
        <Select 
          {...form.getProps("schemeId")}
          label="Схема данных"
          keys={Array.from(mainStore.schemes.keys())}
          options={Array.from(mainStore.schemes.values()).map(scheme => scheme.data.name)}
        />
        { simularContent && simularContent !== content && (
          <Typography fontSize={11} color="primary.main" textAlign="right" whiteSpace="pre-wrap">{
`На сайте уже имеется блок с такой схемой.
Добавьте блоку уникальный ID секции`
          }</Typography>
        )}
        <Select 
          {...form.getProps("slugPrefix", "landings.v2")}
          label="Префикс для ключей"
          keys={[ "landings.v2", "common", "-" ]}
          options={[ "landings.v2", "common", "Без префикса" ]}
        />

        { optionalBlocks && optionalBlocks.length > 0 && (
          <Collapse 
            sx={{ ".MuiCollapse-wrapperInner": { display: "flex", flexWrap: "wrap" } }} 
            label={`Опциональные поля (${optionalBlocks.length})`} 
            disablePadding
          >
            { optionalBlocks.map(([ key, title ]) => (
              <FormControlLabel
                sx={{ flex: "1 0 50%", mx: 0 }}
                key={key} 
                control={<Checkbox 
                  checked={!optionalBlocksSet.has(key)} 
                  onChange={action(e => e.target.checked? optionalBlocksSet.delete(key): optionalBlocksSet.add(key))} 
                />} 
                label={title} 
              />
            ))}
          </Collapse>
        )}
      </DialogContent>

      <DialogActions>
        <Button disabled={disabled} variant="contained" color="secondary" sx={{ px: 6 }} onClick={apply}>
          { (content && !isCopy)? "Изменить данные": "Добавить блок" }
        </Button>
      </DialogActions>
    </>
  )
})

export default NewContentBlockDialog